import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarGrid, type CalendarBooking, type CalendarResource, type CalendarView } from "@/components/calendar/calendar-grid";
import { apiFetch, ApiError } from "@/lib/api-client";
import {
  DAY_END_MINUTES,
  DAY_START_MINUTES,
  SLOT_MINUTES,
  useCalendarDrag,
} from "@/hooks/use-calendar-drag";

interface CalendarFeedResponse {
  data: {
    resources: Array<{ id: string; name: string; type: string; color: string }>;
    bookings: Array<{
      id: string;
      serviceId: string | null;
      title: string;
      startAt: string;
      endAt: string;
      status: CalendarBooking["status"];
      resourceId: string | null;
      assignedResourceIds: string[];
      contactName: string | null;
      notes: string | null;
    }>;
  };
}

interface ServiceOption {
  id: string;
  name: string;
  durationMinutes: number;
  requiredResources?: Array<{ resourceType: string; role?: string; quantity: number }>;
}

interface ResourceRuntime {
  id: string;
  name: string;
  resourceType: string;
  capacity: number;
  isActive: boolean;
}

interface ContactOption {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

interface BookingDraft {
  serviceId: string;
  contactId: string;
  resourceId: string;
  selectedAssignments: Record<string, string>;
  startAt: string;
  endAt: string;
}

interface WalkInDraft {
  customerName: string;
  serviceId: string;
}

interface BookingDragTarget {
  resourceId: string | null;
  assignedResourceIds?: string[];
}

interface AvailabilitySlotResponse {
  data: {
    slots: Array<{
      startTime: string;
      endTime: string;
      resourceId: string;
      assignedResources?: Array<{ resourceId: string; resourceType?: string; role?: string }>;
    }>;
  };
}

const EMPTY_BOOKING_DRAFT: BookingDraft = {
  serviceId: "",
  contactId: "",
  resourceId: "",
  selectedAssignments: {},
  startAt: "",
  endAt: "",
};

function requirementKey(resourceType: string, index: number) {
  return `${resourceType}:${index}`;
}

function buildRequiredResourceEntries(service: ServiceOption | undefined) {
  const requirements = service?.requiredResources?.length
    ? service.requiredResources
    : [{ resourceType: "person", role: "Practitioner", quantity: 1 }];

  return requirements.flatMap((requirement) =>
    Array.from({ length: Math.max(1, requirement.quantity) }, (_, index) => ({
      key: requirementKey(requirement.resourceType, index),
      resourceType: requirement.resourceType,
      role: requirement.role,
      label: requirement.role
        ?? `${requirement.resourceType.charAt(0).toUpperCase()}${requirement.resourceType.slice(1)}${requirement.quantity > 1 ? ` ${index + 1}` : ""}`,
    })),
  );
}

function formatDateTimeLocal(value: Date) {
  const local = new Date(value.getTime() - (value.getTimezoneOffset() * 60_000));
  return local.toISOString().slice(0, 16);
}

function contactLabel(contact: ContactOption) {
  return [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.email || "Unknown";
}

function windowForView(date: Date, view: CalendarView) {
  const from = new Date(date);
  from.setHours(0, 0, 0, 0);

  const to = new Date(from);
  if (view === "day") {
    return { from, to };
  } else if (view === "week") {
    to.setDate(to.getDate() + 6);
  } else {
    const first = new Date(date.getFullYear(), date.getMonth(), 1);
    const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const firstVisible = new Date(first);
    firstVisible.setDate(first.getDate() - first.getDay());
    const lastVisible = new Date(last);
    lastVisible.setDate(last.getDate() + (6 - last.getDay()));
    return { from: firstVisible, to: lastVisible };
  }

  return { from, to };
}

function clampDraftWindow(startAt: Date, endAt: Date) {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const minStart = new Date(start);
  minStart.setHours(0, 0, 0, 0);
  minStart.setMinutes(DAY_START_MINUTES);
  const maxEnd = new Date(start);
  maxEnd.setHours(0, 0, 0, 0);
  maxEnd.setMinutes(DAY_END_MINUTES);

  if (start < minStart) {
    start.setTime(minStart.getTime());
  }
  if (end > maxEnd) {
    end.setTime(maxEnd.getTime());
  }
  if (end <= start) {
    end.setTime(start.getTime() + (SLOT_MINUTES * 60_000));
  }

  return { start, end };
}

function buildNextAssignedResources(booking: BookingDragTarget, targetResourceId: string) {
  const assignedResourceIds = booking.assignedResourceIds ?? [];

  if (assignedResourceIds.length === 0) {
    return [{ resourceId: targetResourceId }];
  }

  let replaced = false;
  const nextAssignments = assignedResourceIds.map((resourceId) => {
    if (!replaced && resourceId === booking.resourceId) {
      replaced = true;
      return { resourceId: targetResourceId };
    }

    return { resourceId };
  });

  if (!replaced) {
    nextAssignments[0] = { resourceId: targetResourceId };
  }

  return nextAssignments;
}

function canCancelBooking(status: CalendarBooking["status"]) {
  return status === "confirmed" || status === "pending";
}

export default function BookingCalendarPage() {
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [view, setView] = useState<CalendarView>("day");
  const [resources, setResources] = useState<CalendarResource[]>([]);
  const [allResources, setAllResources] = useState<ResourceRuntime[]>([]);
  const [bookings, setBookings] = useState<CalendarBooking[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [walkInServices, setWalkInServices] = useState<ServiceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyMessage, setBusyMessage] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [bookingDraft, setBookingDraft] = useState<BookingDraft>(EMPTY_BOOKING_DRAFT);
  const [contactQuery, setContactQuery] = useState("");
  const [contactOptions, setContactOptions] = useState<ContactOption[]>([]);
  const [selectedContactLabel, setSelectedContactLabel] = useState("");
  const [availableSlots, setAvailableSlots] = useState<AvailabilitySlotResponse["data"]["slots"]>([]);

  const [showWalkInModal, setShowWalkInModal] = useState(false);
  const [walkInDraft, setWalkInDraft] = useState<WalkInDraft>({ customerName: "", serviceId: "" });

  const loadCalendar = useCallback(async () => {
    const window = windowForView(selectedDate, view);
    setLoading(true);

    try {
      const [calendarResponse, servicesResponse, walkInResponse, resourceResponse] = await Promise.all([
        apiFetch<CalendarFeedResponse>(
          `/crm/bookings/calendar?from=${encodeURIComponent(window.from.toISOString())}&to=${encodeURIComponent(window.to.toISOString())}`,
        ),
        apiFetch<{ data: ServiceOption[] }>("/crm/bookings/services/intake"),
        apiFetch<{ data: ServiceOption[] }>("/crm/bookings/walk-in/services"),
        apiFetch<{ resources: ResourceRuntime[] }>("/crm/bookings/resources"),
      ]);

      setResources(calendarResponse.data.resources.map((resource) => ({
        id: resource.id,
        name: resource.name,
        type: resource.type,
        color: resource.color,
      })));
      setBookings(calendarResponse.data.bookings.map((booking) => ({
        id: booking.id,
        title: booking.title,
        startTime: new Date(booking.startAt),
        endTime: new Date(booking.endAt),
        resourceId: booking.resourceId,
        resourceName: null,
        contactName: booking.contactName,
        status: booking.status,
        serviceId: booking.serviceId,
        assignedResourceIds: booking.assignedResourceIds,
      })));
      setServices(servicesResponse.data ?? []);
      setWalkInServices(walkInResponse.data ?? []);
      setAllResources((resourceResponse.resources ?? []).filter((resource) => resource.isActive));
      setError(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to load booking calendar");
      setResources([]);
      setBookings([]);
      setAllResources([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, view]);

  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar]);

  const selectedService = useMemo(
    () => services.find((service) => service.id === bookingDraft.serviceId),
    [bookingDraft.serviceId, services],
  );

  const requiredResourceEntries = useMemo(
    () => buildRequiredResourceEntries(selectedService),
    [selectedService],
  );

  useEffect(() => {
    if (!showCreateModal || !bookingDraft.serviceId || !bookingDraft.startAt || !bookingDraft.endAt) {
      setAvailableSlots([]);
      return;
    }

    const start = new Date(bookingDraft.startAt);
    const dayStart = new Date(start);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);

    let cancelled = false;
    void apiFetch<AvailabilitySlotResponse>(
      `/crm/bookings/availability/${bookingDraft.serviceId}?from=${encodeURIComponent(dayStart.toISOString())}&to=${encodeURIComponent(dayEnd.toISOString())}`,
    )
      .then((response) => {
        if (!cancelled) {
          setAvailableSlots(response.data.slots ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableSlots([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [bookingDraft.endAt, bookingDraft.serviceId, bookingDraft.startAt, showCreateModal]);

  const matchingSlots = useMemo(() => {
    if (!bookingDraft.startAt || !bookingDraft.endAt) {
      return [];
    }

    const startIso = new Date(bookingDraft.startAt).toISOString();
    const endIso = new Date(bookingDraft.endAt).toISOString();
    return availableSlots.filter((slot) => slot.startTime === startIso && slot.endTime === endIso);
  }, [availableSlots, bookingDraft.endAt, bookingDraft.startAt]);

  const availableResourceOptions = useMemo(() => {
    return requiredResourceEntries.reduce<Record<string, ResourceRuntime[]>>((accumulator, entry) => {
      const compatibleSlots = matchingSlots.filter((slot) => {
        const assignmentIds = new Set((slot.assignedResources ?? []).map((assignment) => assignment.resourceId));
        return requiredResourceEntries.every((candidate) => {
          if (candidate.key === entry.key) {
            return true;
          }
          const selectedId = bookingDraft.selectedAssignments[candidate.key];
          return !selectedId || assignmentIds.has(selectedId);
        });
      });

      const resourceIds = new Set(
        compatibleSlots.flatMap((slot) => (slot.assignedResources ?? [])
          .filter((assignment) => assignment.resourceType === entry.resourceType)
          .map((assignment) => assignment.resourceId)),
      );

      const options = allResources.filter((resource) => (
        resource.resourceType === entry.resourceType
        && (resourceIds.size === 0 || resourceIds.has(resource.id))
      ));

      accumulator[entry.key] = options;
      return accumulator;
    }, {});
  }, [allResources, bookingDraft.selectedAssignments, matchingSlots, requiredResourceEntries]);

  const hasAllRequiredAssignments = requiredResourceEntries.every((entry) => Boolean(bookingDraft.selectedAssignments[entry.key]));

  const { dragState, dragPreview, startCreateDrag, startMoveDrag, startResizeDrag, updateDrag, completeDrag, cancelDrag } = useCalendarDrag(
    selectedDate,
    async (result) => {
      if (result.operation === "create") {
        const fallbackResourceId = result.resourceId ?? resources[0]?.id ?? "";
        setBookingDraft({
          serviceId: "",
          contactId: "",
          resourceId: fallbackResourceId,
          selectedAssignments: {},
          startAt: formatDateTimeLocal(result.startTime),
          endAt: formatDateTimeLocal(result.endTime),
        });
        setSelectedContactLabel("");
        setContactQuery("");
        setContactOptions([]);
        setShowCreateModal(true);
        return;
      }

      if (!result.bookingId) {
        return;
      }

      setBusyMessage(result.operation === "move" ? "Rescheduling booking..." : "Updating booking duration...");
      try {
        const booking = bookings.find((item) => item.id === result.bookingId);
        const resourceChanged = result.operation === "move"
          && booking
          && Boolean(result.resourceId)
          && result.resourceId !== booking.resourceId;

        let assignedResources: Array<{ resourceId: string }> | undefined;
        if (resourceChanged && booking && result.resourceId) {
          if (!booking.serviceId) {
            throw new Error("Booking service is missing, so staff reassignment cannot be validated");
          }

          const windowStart = new Date(result.startTime);
          windowStart.setHours(0, 0, 0, 0);
          const windowEnd = new Date(windowStart);
          windowEnd.setHours(23, 59, 59, 999);

          const availability = await apiFetch<AvailabilitySlotResponse>(
            `/crm/bookings/availability/${booking.serviceId}?from=${encodeURIComponent(windowStart.toISOString())}&to=${encodeURIComponent(windowEnd.toISOString())}`,
          );

          const slotAvailable = (availability.data.slots ?? []).some((slot) => (
            slot.resourceId === result.resourceId
            && slot.startTime === result.startTime.toISOString()
            && slot.endTime === result.endTime.toISOString()
          ));

          if (!slotAvailable) {
            throw new Error("Target practitioner is not available for this slot");
          }

          assignedResources = buildNextAssignedResources(booking, result.resourceId);
        }

        await apiFetch(`/crm/bookings/${result.bookingId}/reschedule`, {
          method: "POST",
          body: JSON.stringify({
            startTime: result.startTime.toISOString(),
            endTime: result.endTime.toISOString(),
            ...(assignedResources ? { assignedResources } : {}),
          }),
        });
        await loadCalendar();
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : "Failed to update booking");
      } finally {
        setBusyMessage(null);
      }
    },
  );

  useEffect(() => {
    if (!dragState.active) {
      return;
    }

    const handlePointerUp = () => {
      completeDrag();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        cancelDrag();
      }
    };

    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [cancelDrag, completeDrag, dragState.active]);

  useEffect(() => {
    if (contactQuery.trim().length < 2 || bookingDraft.contactId) {
      setContactOptions([]);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        const response = await apiFetch<{ data: { items: ContactOption[] } }>(
          `/crm/people?search=${encodeURIComponent(contactQuery)}&limit=5`,
          { signal: controller.signal },
        );
        if (!controller.signal.aborted) {
          setContactOptions(response.data.items ?? []);
        }
      } catch {
        if (!controller.signal.aborted) {
          setContactOptions([]);
        }
      }
    }, 200);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [bookingDraft.contactId, contactQuery]);

  const primaryResourceId = useMemo(
    () => resources[0]?.id ?? "",
    [resources],
  );

  function openCreateModalForSlot(date: Date, minuteOfDay: number, resourceId?: string) {
    const startAt = new Date(date);
    startAt.setHours(0, 0, 0, 0);
    startAt.setMinutes(minuteOfDay);
    const endAt = new Date(startAt.getTime() + (60 * 60_000));
    const clamped = clampDraftWindow(startAt, endAt);

    setBookingDraft({
      serviceId: "",
      contactId: "",
      resourceId: resourceId ?? primaryResourceId,
      selectedAssignments: {},
      startAt: formatDateTimeLocal(clamped.start),
      endAt: formatDateTimeLocal(clamped.end),
    });
    setSelectedContactLabel("");
    setContactQuery("");
    setContactOptions([]);
    setShowCreateModal(true);
  }

  async function handleCreateBooking() {
    setBusyMessage("Creating booking...");
    try {
      const selectedAssignments = Object.values(bookingDraft.selectedAssignments).filter(Boolean);
      if (selectedAssignments.length === 0) {
        throw new Error("Select the required available resources before creating the booking");
      }

      await apiFetch("/crm/bookings/book", {
        method: "POST",
        body: JSON.stringify({
          serviceId: bookingDraft.serviceId,
          assignedResources: selectedAssignments.map((resourceId) => ({ resourceId })),
          contactId: bookingDraft.contactId,
          startTime: new Date(bookingDraft.startAt).toISOString(),
          endTime: new Date(bookingDraft.endAt).toISOString(),
        }),
      });
      setShowCreateModal(false);
      setBookingDraft(EMPTY_BOOKING_DRAFT);
      setAvailableSlots([]);
      await loadCalendar();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : reason instanceof Error ? reason.message : "Failed to create booking");
    } finally {
      setBusyMessage(null);
    }
  }

  async function handleCreateWalkIn() {
    setBusyMessage("Creating walk-in...");
    try {
      await apiFetch("/crm/bookings/walk-in/create", {
        method: "POST",
        body: JSON.stringify({
          customerName: walkInDraft.customerName.trim() || null,
          serviceId: walkInDraft.serviceId || null,
        }),
      });
      setShowWalkInModal(false);
      setWalkInDraft({ customerName: "", serviceId: "" });
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : reason instanceof Error ? reason.message : "Failed to create walk-in");
    } finally {
      setBusyMessage(null);
    }
  }

  async function handleCancelBooking(booking: CalendarBooking) {
    if (!canCancelBooking(booking.status)) {
      return;
    }

    const confirmed = window.confirm(`Cancel ${booking.title} for ${booking.contactName ?? "this contact"}?`);
    if (!confirmed) {
      return;
    }

    setBusyMessage("Cancelling booking...");
    setError(null);
    try {
      await apiFetch(`/crm/bookings/${booking.id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason: "Cancelled from staff calendar" }),
      });
      await loadCalendar();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : reason instanceof Error ? reason.message : "Failed to cancel booking");
    } finally {
      setBusyMessage(null);
    }
  }

  if (loading) {
    return (
      <div className="rounded-3xl border border-border bg-background p-8 text-sm text-muted-foreground">
        Loading mounted booking calendar...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-3xl border border-border bg-background p-6 shadow-[0_24px_70px_-50px_rgba(15,23,42,0.45)] lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-3xl space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-primary)]">Mounted booking calendar</p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Staff booking calendar</h1>
          <p className="text-sm leading-6 text-muted-foreground">
            This is the live operator calendar for day, week, and month planning. Drag empty slots to prefill a booking, drag cards to reschedule, resize from the booking edge, and create walk-ins on the same CRM seam.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowWalkInModal(true)}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
          >
            Start walk-in
          </button>
          <button
            type="button"
            onClick={() => openCreateModalForSlot(selectedDate, DAY_START_MINUTES, primaryResourceId)}
            className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            New booking
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/6 p-4 text-sm text-[var(--color-error)]">
          {error}
        </div>
      ) : null}

      {busyMessage ? (
        <div className="rounded-2xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/8 p-4 text-sm text-[var(--color-primary)]">
          {busyMessage}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-3xl border border-border bg-background">
        <CalendarGrid
          bookings={bookings}
          resources={resources}
          selectedDate={selectedDate}
          view={view}
          onDateChange={setSelectedDate}
          onViewChange={setView}
          onSlotClick={openCreateModalForSlot}
          dragState={dragState}
          dragPreview={dragPreview}
          onSlotPointerDown={(minuteOfDay, resourceId) => startCreateDrag(minuteOfDay, resourceId ?? null)}
          onSlotPointerEnter={(minuteOfDay, resourceId) => updateDrag(minuteOfDay, resourceId ?? null)}
          onCalendarPointerUp={completeDrag}
          onBookingMovePointerDown={(bookingId, minuteOfDay, resourceId) => {
            const booking = bookings.find((item) => item.id === bookingId);
            if (!booking) return;
            startMoveDrag({
              bookingId,
              resourceId: resourceId ?? null,
              bookingStartMinute: (booking.startTime.getHours() * 60) + booking.startTime.getMinutes(),
              bookingEndMinute: (booking.endTime.getHours() * 60) + booking.endTime.getMinutes(),
              pointerMinute: minuteOfDay,
            });
          }}
          onBookingResizePointerDown={(bookingId, startMinute, endMinute, resourceId) => {
            startResizeDrag({
              bookingId,
              resourceId: resourceId ?? null,
              bookingStartMinute: startMinute,
              bookingEndMinute: endMinute,
            });
          }}
        />
      </div>

      <section className="rounded-3xl border border-border bg-background p-5">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--color-primary)]">Appointment lifecycle</p>
            <h2 className="mt-1 text-lg font-semibold text-foreground">Manage today&apos;s appointments</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Create and reschedule on the calendar, cancel active bookings here, and keep completed or no-show bookings visible for follow-up.
            </p>
          </div>
          <a href="/services/bookings/predictions" className="text-sm font-medium text-[var(--color-primary)] hover:underline">
            Review no-show risk
          </a>
        </div>

        <div className="mt-4 divide-y divide-border rounded-2xl border border-border">
          {bookings.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No appointments in this calendar window.</p>
          ) : bookings.map((booking) => (
            <div key={booking.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-foreground">{booking.title}</p>
                <p className="text-xs text-muted-foreground">
                  {booking.contactName ?? "Unassigned contact"} · {booking.status.replace(/_/g, " ")}
                </p>
              </div>
              <button
                type="button"
                disabled={!canCancelBooking(booking.status)}
                onClick={() => void handleCancelBooking(booking)}
                className="rounded-lg border border-[var(--color-error)]/35 px-3 py-2 text-sm font-medium text-[var(--color-error)] hover:bg-[var(--color-error)]/8 disabled:cursor-not-allowed disabled:border-border disabled:text-muted-foreground disabled:hover:bg-transparent"
              >
                {canCancelBooking(booking.status) ? "Cancel booking" : "No cancellation action"}
              </button>
            </div>
          ))}
        </div>
      </section>

      {showCreateModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4" onClick={() => setShowCreateModal(false)}>
          <div className="w-full max-w-2xl rounded-3xl bg-background p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--color-primary)]">Drag-to-create</p>
              <h2 className="text-2xl font-semibold text-foreground">Create booking</h2>
              <p className="text-sm text-muted-foreground">The dragged slot is pre-filled. Choose the service and contact, then confirm the live booking.</p>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <label className="space-y-2 text-sm">
                <span className="font-medium text-foreground">Service</span>
                <select
                  value={bookingDraft.serviceId}
                  onChange={(event) => {
                    const service = services.find((item) => item.id === event.target.value);
                    const startAt = new Date(bookingDraft.startAt);
                    const endAt = new Date(startAt.getTime() + ((service?.durationMinutes ?? 60) * 60_000));
                    const clamped = clampDraftWindow(startAt, endAt);
                    const nextEntries = buildRequiredResourceEntries(service);
                    const nextSelectedAssignments = nextEntries.reduce<Record<string, string>>((accumulator, entry) => {
                      if (entry.resourceType === "person" && bookingDraft.resourceId) {
                        accumulator[entry.key] = bookingDraft.resourceId;
                      }
                      return accumulator;
                    }, {});
                    setBookingDraft((current) => ({
                      ...current,
                      serviceId: event.target.value,
                      selectedAssignments: nextSelectedAssignments,
                      endAt: formatDateTimeLocal(clamped.end),
                    }));
                  }}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2"
                >
                  <option value="">Choose a service</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
              </label>

              {requiredResourceEntries.map((entry) => (
                <label key={entry.key} className="space-y-2 text-sm">
                  <span className="font-medium text-foreground">{entry.label}</span>
                  <select
                    value={bookingDraft.selectedAssignments[entry.key] ?? ""}
                    onChange={(event) => setBookingDraft((current) => ({
                      ...current,
                      resourceId: entry.resourceType === "person" ? event.target.value : current.resourceId,
                      selectedAssignments: {
                        ...current.selectedAssignments,
                        [entry.key]: event.target.value,
                      },
                    }))}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2"
                  >
                    <option value="">Choose available {entry.resourceType}</option>
                    {(availableResourceOptions[entry.key] ?? []).map((resource) => (
                      <option key={resource.id} value={resource.id}>{resource.name}</option>
                    ))}
                  </select>
                </label>
              ))}

              <label className="space-y-2 text-sm md:col-span-2">
                <span className="font-medium text-foreground">Contact</span>
                <input
                  value={bookingDraft.contactId ? selectedContactLabel : contactQuery}
                  onChange={(event) => {
                    setBookingDraft((current) => ({ ...current, contactId: "" }));
                    setSelectedContactLabel("");
                    setContactQuery(event.target.value);
                  }}
                  placeholder="Search a contact by name or email"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2"
                />
                {!bookingDraft.contactId && contactOptions.length > 0 ? (
                  <div className="rounded-2xl border border-border bg-background p-2">
                    {contactOptions.map((contact) => (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => {
                          setBookingDraft((current) => ({ ...current, contactId: contact.id }));
                          setSelectedContactLabel(contactLabel(contact));
                          setContactQuery("");
                          setContactOptions([]);
                        }}
                        className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left hover:bg-muted"
                      >
                        <span className="text-sm text-foreground">{contactLabel(contact)}</span>
                        <span className="text-xs text-muted-foreground">{contact.email}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </label>

              <label className="space-y-2 text-sm">
                <span className="font-medium text-foreground">Start</span>
                <input
                  type="datetime-local"
                  value={bookingDraft.startAt}
                  onChange={(event) => setBookingDraft((current) => ({ ...current, startAt: event.target.value }))}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2"
                />
              </label>

              <label className="space-y-2 text-sm">
                <span className="font-medium text-foreground">End</span>
                <input
                  type="datetime-local"
                  value={bookingDraft.endAt}
                  onChange={(event) => setBookingDraft((current) => ({ ...current, endAt: event.target.value }))}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2"
                />
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setShowCreateModal(false)} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted">
                Cancel
              </button>
              <button
                type="button"
                disabled={!bookingDraft.serviceId || !hasAllRequiredAssignments || !bookingDraft.contactId || !bookingDraft.startAt || !bookingDraft.endAt}
                onClick={() => void handleCreateBooking()}
                className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                Create booking
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showWalkInModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4" onClick={() => setShowWalkInModal(false)}>
          <div className="w-full max-w-lg rounded-3xl bg-background p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--color-primary)]">C-004 walk-in runtime</p>
              <h2 className="text-2xl font-semibold text-foreground">Start a walk-in</h2>
            </div>

            <div className="mt-6 space-y-4">
              <label className="space-y-2 text-sm">
                <span className="font-medium text-foreground">Guest name</span>
                <input
                  value={walkInDraft.customerName}
                  onChange={(event) => setWalkInDraft((current) => ({ ...current, customerName: event.target.value }))}
                  placeholder="Optional guest name"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2"
                />
              </label>
              <label className="space-y-2 text-sm">
                <span className="font-medium text-foreground">Service</span>
                <select
                  value={walkInDraft.serviceId}
                  onChange={(event) => setWalkInDraft((current) => ({ ...current, serviceId: event.target.value }))}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2"
                >
                  <option value="">Choose a walk-in service</option>
                  {walkInServices.map((service) => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setShowWalkInModal(false)} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted">
                Cancel
              </button>
              <button type="button" onClick={() => void handleCreateWalkIn()} className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white">
                Add to queue
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
